import { ctxReport } from '@/contexts/ReportCtx';
import { ReportTabValue } from '@/types';
import { datePeriodMD } from '@/utilities/dateUtils';
import { CalendarMonth, Category, Check } from '@mui/icons-material';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MobileHeader from '@/shared/ui/mobile-header/MobileHeader';
import { MobileHeaderTitle } from '@/shared/ui/mobile-header/MobileHeaderTitle';

export function ReportMobileHeader() {
  const reportCtx = useContext(ctxReport);
  const { t, i18n } = useTranslation();

  if (!reportCtx) return;

  const hasDates = Boolean(reportCtx.selectedDates?.from && reportCtx.selectedDates?.to);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const intervalLength = useMemo(() => {
    if (hasDates) {
      return datePeriodMD(reportCtx.selectedDates!.from, reportCtx.selectedDates!.to, t);
    }
    return '';
  }, [reportCtx?.selectedDates?.from, reportCtx?.selectedDates?.to, i18n, hasDates]);

  const onResultTab = reportCtx.tab === ReportTabValue.entitySelect || reportCtx.tab === ReportTabValue.result;

  function navigateBack() {
    reportCtx?.setTab(reportCtx.tab - 1);
    const isLastTabTargetRelated =
      reportCtx?.lastVisitedTab === 'TargetReportTab' || reportCtx?.lastVisitedTab === 'TargetTab';
    reportCtx?.setLastVisitedTab(isLastTabTargetRelated ? 'TargetTab' : 'DriverTab');
  }

  function getResultTitle() {
    if (reportCtx?.lastVisitedTab === 'TargetTab' || reportCtx?.lastVisitedTab === 'TargetReportTab') {
      return t('report.tab.target.title');
    }

    return t('report.tab.driver.title');
  }

  function getResultSubTitle() {
    if (reportCtx?.lastVisitedTab === 'TargetTab') {
      return t('report.tab.target.subtitle');
    }
    if (reportCtx?.lastVisitedTab === 'DriverTab') {
      return t('report.tab.driver.subtitle');
    }
    if (reportCtx?.lastVisitedTab === 'TargetReportTab' || reportCtx?.lastVisitedTab === 'DriverReportTab') {
      return t('report.tab.report.subtitle');
    }
  }

  const title = onResultTab ? getResultTitle() : t('mainMenu.reports');

  return (
    <>
      <MobileHeader
        title={
          <MobileHeaderTitle data-qa-element-id={onResultTab ? 'report_result_target_title' : 'report_tab_title'}>
            {title}
          </MobileHeaderTitle>
        }
        onBack={onResultTab ? navigateBack : undefined}
      >
        {!onResultTab && (
          <Tabs
            variant="fullWidth"
            value={reportCtx.tab}
            indicatorColor="secondary"
            textColor="secondary"
            onChange={(_evt, value) => reportCtx.setTab(value as ReportTabValue)}
          >
            <Tab
              data-qa-element-id="date_selector_tab"
              value={ReportTabValue.dateSelect}
              icon={
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarMonth />
                  {reportCtx.selectedDates && (
                    <Check color="success" fontSize="medium" sx={{ position: 'absolute', right: -25 }} />
                  )}
                </Box>
              }
              label={
                <div
                  data-qa-element-id={`date_selector_tab_label_${reportCtx.selectedDates?.from.toLocaleDateString()}_${reportCtx.selectedDates?.to.toLocaleDateString()}`}
                >
                  {intervalLength || t('refuels.period')}
                </div>
              }
              sx={{ textTransform: 'none' }}
            />

            <Tab
              data-qa-element-id="target_selector_tab"
              value={ReportTabValue.categorySelect}
              icon={<Category />}
              label={t('tachograph.category.mobileTitle')}
              sx={{ textTransform: 'none', opacity: reportCtx.selectedDates ? 1 : 0 }}
              disabled={reportCtx.selectedDates ? false : true}
            />
          </Tabs>
        )}
      </MobileHeader>
      {onResultTab && (
        <Typography variant="helperText" data-qa-element-id="report_result_target_sub_title">
          {getResultSubTitle()}
        </Typography>
      )}
    </>
  );
}
